<script setup>
import { nextTick, onMounted, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['action', 'close']);
const { t } = useI18n();
const sheetRef = useTemplateRef('sheetRef');

const focusableButtons = () =>
  Array.from(sheetRef.value?.querySelectorAll('button:not([disabled])') || []);

const onKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    emit('close');
    return;
  }
  if (event.key !== 'Tab') return;

  const buttons = focusableButtons();
  if (!buttons.length) return;
  const first = buttons[0];
  const last = buttons.at(-1);
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

onMounted(async () => {
  await nextTick();
  const firstAction = sheetRef.value?.querySelector(
    '[data-mobile-menu-action]:not([disabled])'
  );
  firstAction?.focus();
});

const onItemClick = item => {
  if (item.disabled) return;
  emit('action', item);
};
</script>

<template>
  <div
    class="fixed inset-0 z-50"
    data-testid="mobile-conversation-menu"
    role="dialog"
    aria-modal="true"
    :aria-label="t('CONVERSATION.HEADER.MORE_ACTIONS')"
    @keydown="onKeydown"
  >
    <button
      type="button"
      class="absolute inset-0 bg-black/50"
      :aria-label="t('CONVERSATION.HEADER.CLOSE')"
      data-testid="mobile-conversation-menu-backdrop"
      @click="emit('close')"
    />
    <div
      ref="sheetRef"
      class="absolute inset-x-0 bottom-0 flex max-h-[80vh] flex-col rounded-t-2xl border-t border-n-weak bg-n-solid-1 pb-[env(safe-area-inset-bottom)]"
    >
      <div class="flex items-center justify-between px-4 py-3">
        <p class="m-0 text-[15px] font-medium text-n-slate-12">
          {{ t('CONVERSATION.HEADER.MORE_ACTIONS') }}
        </p>
        <button
          type="button"
          class="flex size-11 shrink-0 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
          :aria-label="t('CONVERSATION.HEADER.CLOSE')"
          data-testid="mobile-conversation-menu-close"
          @click="emit('close')"
        >
          <span class="i-ph-x size-5" aria-hidden="true" />
        </button>
      </div>
      <ul class="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        <template v-for="item in props.items" :key="item.action">
          <li
            v-if="item.dividerBefore"
            role="separator"
            class="my-2 border-t border-n-weak"
            data-testid="mobile-menu-divider"
          />
          <li>
            <button
              type="button"
              class="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-[14px] font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="item.disabled"
              :data-testid="`mobile-menu-${item.action}`"
              :data-mobile-menu-action="item.action"
              @click="onItemClick(item)"
            >
              <span
                :class="item.icon"
                class="size-5 shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
              <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
            </button>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>
